import { Box, Button, Grid, Link, Typography, useTheme } from "@mui/material";
import { Fragment } from "react/jsx-runtime";
import { Code } from "~/components/code";
import { Command } from "~/components/guides/components/command";
import { Config } from "~/constants/configuration_provider";
import {
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { TokenCustomizer } from "~/components/token-customizer";
import { roleDeploymentManager } from "~/lib/iam/roles/DeploymentManager";
import { EmptyState } from "~/pages/inventory/components/EmptyState";
import { useToken } from "~/providers/token";
import { NoPermissionsIcon } from "~/icons";
import { OrganizationScope, SpaceScope } from "~/hooks/useScope";

interface Ms365AutomatedFormProps {
  hasAutomatedDeploymentPermissions: boolean;
  scope: SpaceScope | OrganizationScope;
  defaultIntegrationName: string;
  setView: React.Dispatch<React.SetStateAction<"setup" | "add policies">>;
}

export function Ms365AutomatedForm({
  hasAutomatedDeploymentPermissions,
  scope,
  defaultIntegrationName,
  setView,
}: Ms365AutomatedFormProps) {
  const theme = useTheme();
  const { token } = useToken({
    ...(hasAutomatedDeploymentPermissions && {
      roles: [{ mrn: roleDeploymentManager.mrn }],
    }),
  });

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Automatically create a Mondoo Microsoft 365 integration by pasting a
          command in Azure Cloud Shell.
          <br />
          To learn about automated vs. manual setup options and permissions
          required, read the{" "}
          <Link
            href="https://mondoo.com/docs/infra/saas/m365/overview"
            target="_blank"
            rel="noopener"
          >
            Mondoo documentation
          </Link>
          .
        </Typography>
      </Grid>

      {/* Step 2 */}
      <Grid size={12} sx={{ pb: 1 }}>
        <Command
          number={1}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Copy the installation command
        </Command>

        <Box pl={6}>
          <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
            You can replace the string following --integration-name with your
            desired integration name if you wish.
          </Typography>

          {hasAutomatedDeploymentPermissions && (
            <Fragment>
              <Code className="bash" copyButton>
                {`bash -c "$(curl -sSL https://${Config.VITE_INSTALL_HOST}/sh)"\ncnspec login -t '${token.value}'\ncnspec integrate ms365 --space ${scope.id} --integration-name "${defaultIntegrationName}"`}
              </Code>
              <Box mt={-1}>
                <TokenCustomizer />
              </Box>
            </Fragment>
          )}
          {/* If a user does not have permissions for the automated token, we show a permissions issue */}
          {!hasAutomatedDeploymentPermissions && (
            <Grid size={12} sx={{ pb: 1 }}>
              <EmptyState
                headline="Additional privileges required"
                icon={<NoPermissionsIcon sx={{ fontSize: 48 }} />}
                textContent=" Please contact an administrator."
              />
            </Grid>
          )}
        </Box>
      </Grid>

      {/* Step 3 */}
      <Grid pb={4}>
        <Command
          number={3}
          options={{
            fontSize: { xs: 16 },
            dotColor: theme.palette.background.lightest,
          }}
        >
          Paste it into your Azure Cloud Shell
        </Command>
        <Box pl={6}>
          <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
            Paste the command into your target Azure Cloud Shell.
            <br />
            To learn more, read the{" "}
            <Link
              href="https://mondoo.com/docs/infra/saas/m365/overview"
              target="_blank"
              rel="noopener"
            >
              Mondoo documentation
            </Link>
            .
          </Typography>

          <Button
            href="https://shell.azure.com"
            target="_blank"
            variant="outlined"
            color="secondary"
            endIcon={<OpenInNewIcon />}
          >
            AZURE CLOUD SHELL
          </Button>
        </Box>
      </Grid>

      <Grid size={12} sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          onClick={() => setView("add policies")}
          variant="contained"
          color="primary"
          endIcon={<ChevronRightIcon />}
        >
          start scanning
        </Button>
      </Grid>
    </Grid>
  );
}
